/**
 * F43 (Track B) — admin consumer-user management API client.
 *
 * The consumer-user surface lives under `/admin/platform-users/*` (NOT
 * `/admin/users` — that path collided with F06 admin-staff). The list/detail +
 * per-tab read-throughs + the suspend/restore/note/device-revoke mutations hit
 * the Track-A routes; the central report queue + GDPR live in their own clients
 * (`reports.ts`, `gdpr.ts`).
 *
 * Component tests drive `adminUsers.fixture.ts` (swapped via the vitest alias);
 * production code uses these real `adminFetch` calls. Permission gating is
 * server-side; <ProtectedRoute> + in-page checks gate navigation/affordances.
 *
 * Suspend/restore carry the `If-Match: <users.updatedAt ISO>` header (F12
 * two-phase pattern); a race → `409 STALE_USER` surfaced via `ApiError.code`.
 */
import type {
  AdminCreateUserInput,
  AdminEditUserInput,
  AdminPageMeta,
  AdminUserDetail,
  AdminUserListItem,
  AdminUserVerificationFilter,
  BulkActionInput,
  BulkActionResponse,
  DeviceToken,
  GdprRequest,
  NameLockOverride,
  ReportListItem,
  SuspendUserInput,
  UserStatus,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient';

// ─── Query + response shapes (the contract Track A must satisfy) ──────────────

export interface AdminUserListQuery {
  readonly q?: string;
  /** B2C | venue_owner | trainer | club_admin | tournament_organizer. */
  readonly accountType?: string;
  readonly status?: UserStatus | '';
  readonly hasActiveReports?: boolean;
  readonly suspensionHistory?: boolean;
  /**
   * F09 v2 (E41) — `any` = holds a player OR trainer tick; `player` = holds the
   * player tick. Sent as `?verification=`. Replaces the v1 `verificationStatus`.
   */
  readonly verification?: AdminUserVerificationFilter;
  readonly city?: string;
  readonly sport?: string;
  readonly cursor?: string;
  readonly limit?: number;
  /** 1-based page index (page-numbered pagination). */
  readonly page?: number;
  /** Page size (default 25 server-side, max 100). */
  readonly perPage?: number;
}

export interface AdminUserListMeta {
  readonly total: number;
  /** KPI counters surfaced as Stat cards on the list page. */
  readonly signupsToday: number;
  readonly activeReports: number;
  readonly pendingBlueTicks: number;
  readonly pendingGdpr: number;
  readonly nextCursor: string | null;
  /** Present when `page` is supplied (page-numbered pagination). */
  readonly pagination?: AdminPageMeta;
}

export interface AdminUserListResponse {
  readonly data: readonly AdminUserListItem[];
  readonly meta: AdminUserListMeta;
}

/** A1's GET /admin/platform-users/:id returns the detail under a `data` envelope. */
interface DetailEnvelope {
  readonly data: AdminUserDetail;
}

/**
 * Admin Super-Powers — POST /admin/platform-users (super_admin, `users:create`)
 * result. Mints a brand-new CONSUMER user in `pending_verification` + returns the
 * one-time activation URL (the email link the new user redeems via
 * `/auth/invite/accept`). The same link is emailed by the server, so the admin
 * can copy it directly (a fallback when email delivery is delayed).
 */
export interface AdminCreateUserResult {
  readonly user: AdminUserDetail;
  readonly activationUrl: string;
}

/**
 * Loose per-tab read-through rows. Each tab is a read-through into the owning
 * feature (F17/F29/F36/F33-35/F37/F26); the admin page renders them as a simple
 * label + secondary line + timestamp, so the client keeps them deliberately
 * loose (the owning feature owns the rich shape on its own surface).
 */
export interface AdminUserTabRow {
  readonly id: string;
  readonly title: string;
  readonly subtitle?: string | null;
  readonly status?: string | null;
  readonly at?: string | null;
  /** Optional in-admin deep-link target for the row. */
  readonly href?: string | null;
  /**
   * Teams tab only (PU-13) — the TEAM's own lifecycle status
   * ('active' | 'suspended' | 'disbanded'), distinct from the row's membership
   * `status`. Lets the panel hide team-wide actions that can never succeed.
   */
  readonly teamStatus?: string | null;
}

/** The `badges` sub-section row — adds `isCatalogBadge` (Revoke only on catalog badges). */
export interface AdminUserBadgeRow extends AdminUserTabRow {
  readonly isCatalogBadge: boolean;
}

export interface AdminUserTabResponse {
  readonly data: readonly AdminUserTabRow[];
  readonly nextCursor?: string | null;
}

export interface AdminUserAuditEntry {
  readonly id: string;
  readonly action: string;
  readonly actorAdminId: string | null;
  /** The acting admin's display name, else email; null for system rows (N36). */
  readonly actorDisplay: string | null;
  readonly reason: string | null;
  readonly fieldPath: string | null;
  readonly afterSnapshot?: Record<string, unknown> | null;
  readonly createdAt: string;
}

export interface AdminUserAuditResponse {
  readonly data: readonly AdminUserAuditEntry[];
  readonly nextCursor: string | null;
}

/** A read-through of the user's reports-filed / reports-against tab. */
export interface UserReportsTabResponse {
  readonly data: readonly ReportListItem[];
  readonly nextCursor: string | null;
}

export interface UserGdprTabResponse {
  readonly data: readonly GdprRequest[];
}

export interface UserDevicesResponse {
  readonly data: readonly DeviceToken[];
}

/**
 * The per-tab keys whose data is a flat `AdminUserTabRow[]` fetched lazily via
 * the generic tab endpoint. `trainer` + `reviews-badges` are NOT here — they
 * have richer shapes + dedicated client methods + dedicated panels.
 */
export type AdminUserGenericTab = 'bookings' | 'teams' | 'affiliations' | 'suspension-history';

/**
 * The `trainer` tab payload — distinguishes "not a trainer" from "no profile yet".
 * F09 v2 — render the trainer tick from `AdminUserDetail.trainerVerified` (§13).
 */
export interface AdminUserTrainerTab {
  readonly isTrainer: boolean;
  readonly profile: {
    readonly headline: string | null;
    readonly bio: string | null;
    readonly yearsExperience: number | null;
    readonly acceptingNewClients: boolean;
  } | null;
}

/**
 * `PATCH /admin/platform-users/:id/fields` body: the frozen edit input plus the
 * F09 v2 name-lock override (§11 point 2). The override needs
 * `verifications:review` on top of `users:edit`; the server checks both.
 */
export type AdminEditUserRequest = AdminEditUserInput & {
  readonly nameLockOverride?: NameLockOverride;
};

/** The `reviews-badges` tab payload — three labelled sub-sections of display rows. */
export interface AdminUserReviewsBadgesTab {
  readonly reviewsWritten: readonly AdminUserTabRow[];
  readonly reviewsReceived: readonly AdminUserTabRow[];
  readonly badges: readonly AdminUserBadgeRow[];
}

/** One internal note for the `notes` tab. */
export interface AdminUserNoteRow {
  readonly id: string;
  readonly note: string;
  readonly author: string;
  readonly createdAt: string;
}

/** One per-sport skill row for the `skills` tab. */
export interface AdminUserSkillRow {
  readonly sportName: string;
  readonly isPrimary: boolean;
  readonly claimedSkillValue: number | null;
  readonly effectiveSkillValue: number | null;
  readonly skillSource: string;
  readonly skillLastUpdatedAt: string | null;
  readonly matchesPlayed: number;
  readonly wins: number;
  readonly losses: number;
  readonly draws: number;
}

/** Presigned upload ticket for an avatar (request → PUT → finalize). */
export interface AvatarUploadTicket {
  readonly url: string;
  readonly headers: Readonly<Record<string, string>>;
  readonly s3Key: string;
  readonly expiresAt: string;
}

/**
 * F43-FC §6.6 — the suspend/end admin-affiliation response (the affiliation row
 * + the cascade count of future bookings cancelled).
 */
export interface AdminAffiliationMutationResult {
  readonly id: string;
  readonly venueId: string;
  readonly trainerUserId: string;
  readonly status: string;
  readonly cancelledBookingCount: number;
}

export const adminUsersApi = {
  async list(query: AdminUserListQuery): Promise<AdminUserListResponse> {
    const qs = buildQuery({
      q: query.q,
      // PU-04 — the UI's 'B2C' option maps to the real `consumer` role; no user
      // ever carries a literal 'B2C' role, so sending it always matched nothing.
      accountType: query.accountType === 'B2C' ? 'consumer' : query.accountType,
      status: query.status || undefined,
      hasActiveReports: query.hasActiveReports,
      suspensionHistory: query.suspensionHistory,
      verification: query.verification,
      city: query.city,
      sport: query.sport,
      cursor: query.cursor,
      limit: query.limit,
      page: query.page,
      perPage: query.perPage,
    });
    return adminFetch<AdminUserListResponse>(`/admin/platform-users${qs}`);
  },

  /**
   * Admin Super-Powers — POST /admin/platform-users (super_admin, `users:create`).
   * Creates a CONSUMER user + issues an activation invite. 409 EMAIL_TAKEN when
   * the email already belongs to a user; 422 VALIDATION_ERROR on a bad body.
   * Business roles are NOT settable here — provision a B2B actor instead
   * (`adminB2bAccountApi.createAccount`).
   */
  async createUser(input: AdminCreateUserInput): Promise<AdminCreateUserResult> {
    const res = await adminFetch<{ data: AdminCreateUserResult }>('/admin/platform-users', {
      method: 'POST',
      body: input,
    });
    return res.data;
  },

  async getDetail(userId: string): Promise<AdminUserDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/platform-users/${encodeURIComponent(userId)}`,
    );
    return res.data;
  },

  async getTab(
    userId: string,
    tab: AdminUserGenericTab,
    cursor?: string,
  ): Promise<AdminUserTabResponse> {
    const qs = buildQuery({ cursor });
    return adminFetch<AdminUserTabResponse>(
      `/admin/platform-users/${encodeURIComponent(userId)}/${tab}${qs}`,
    );
  },

  async getReportsFiled(userId: string, cursor?: string): Promise<UserReportsTabResponse> {
    const qs = buildQuery({ cursor });
    return adminFetch<UserReportsTabResponse>(
      `/admin/platform-users/${encodeURIComponent(userId)}/reports-filed${qs}`,
    );
  },

  async getReportsAgainst(userId: string, cursor?: string): Promise<UserReportsTabResponse> {
    const qs = buildQuery({ cursor });
    return adminFetch<UserReportsTabResponse>(
      `/admin/platform-users/${encodeURIComponent(userId)}/reports-against${qs}`,
    );
  },

  async getGdpr(userId: string): Promise<UserGdprTabResponse> {
    return adminFetch<UserGdprTabResponse>(
      `/admin/platform-users/${encodeURIComponent(userId)}/gdpr`,
    );
  },

  async getDevices(userId: string): Promise<UserDevicesResponse> {
    return adminFetch<UserDevicesResponse>(
      `/admin/platform-users/${encodeURIComponent(userId)}/devices`,
    );
  },

  async getAudit(userId: string, cursor?: string): Promise<AdminUserAuditResponse> {
    const qs = buildQuery({ cursor });
    return adminFetch<AdminUserAuditResponse>(
      `/admin/platform-users/${encodeURIComponent(userId)}/audit${qs}`,
    );
  },

  async getTrainer(userId: string): Promise<{ data: AdminUserTrainerTab }> {
    return adminFetch<{ data: AdminUserTrainerTab }>(
      `/admin/platform-users/${encodeURIComponent(userId)}/trainer`,
    );
  },

  async getReviewsBadges(userId: string): Promise<{ data: AdminUserReviewsBadgesTab }> {
    return adminFetch<{ data: AdminUserReviewsBadgesTab }>(
      `/admin/platform-users/${encodeURIComponent(userId)}/reviews-badges`,
    );
  },

  async getSkills(userId: string): Promise<{ data: readonly AdminUserSkillRow[] }> {
    return adminFetch<{ data: readonly AdminUserSkillRow[] }>(
      `/admin/platform-users/${encodeURIComponent(userId)}/skills`,
    );
  },

  async getNotes(userId: string): Promise<{ data: readonly AdminUserNoteRow[] }> {
    return adminFetch<{ data: readonly AdminUserNoteRow[] }>(
      `/admin/platform-users/${encodeURIComponent(userId)}/notes`,
    );
  },

  /** Delete one internal note (super-admin only — users:delete). */
  async deleteNote(userId: string, noteId: string): Promise<void> {
    await adminFetch<{ data: { id: string; deleted: boolean } }>(
      `/admin/platform-users/${encodeURIComponent(userId)}/notes/${encodeURIComponent(noteId)}`,
      { method: 'DELETE' },
    );
  },

  // PU-15 — the dead `verifySkill` client method (F08 POST /admin/users/:userId/
  // skill-verify) was removed: no page or component called it (the Skills panel
  // uses addSkill/upsertSkill/removeSkill exclusively). The backend endpoint
  // stays — the F08 route + service are untouched.

  /** F43-FC §6.1 — add a sport (claimed 1.0–5.0). 409 SPORT_ALREADY_ADDED. */
  async addSkill(
    userId: string,
    input: { sport: string; claimedSkillValue: number; isPrimary?: boolean },
  ): Promise<void> {
    await adminFetch(`/admin/users/${encodeURIComponent(userId)}/skills`, {
      method: 'POST',
      body: input,
    });
  },

  /**
   * F43-FC §6.1 — upsert one sport: claimed (1.0–5.0) / effective (1.0–7.0) /
   * skillSource / isPrimary / W-L-D + matches-played counters. `reason` (≥10)
   * required when effective OR a counter changes.
   */
  async upsertSkill(
    userId: string,
    sport: string,
    input: {
      claimedSkillValue?: number;
      effectiveSkillValue?: number;
      skillSource?: string;
      isPrimary?: boolean;
      wins?: number;
      losses?: number;
      draws?: number;
      matchesPlayed?: number;
      reason?: string;
    },
  ): Promise<void> {
    await adminFetch(
      `/admin/users/${encodeURIComponent(userId)}/skills/${encodeURIComponent(sport)}`,
      { method: 'PUT', body: input },
    );
  },

  /** F43-FC §6.1 — soft-remove a sport (preserves counters + rating history). */
  async removeSkill(userId: string, sport: string): Promise<void> {
    await adminFetch(
      `/admin/users/${encodeURIComponent(userId)}/skills/${encodeURIComponent(sport)}`,
      { method: 'DELETE' },
    );
  },

  /**
   * Super-admin comprehensive edit (users:edit). If-Match round-trip → 409
   * STALE_USER. A `displayName` change on a ticked user → 422
   * NAME_LOCKED_VERIFIED unless the body carries `nameLockOverride`.
   */
  async editFields(
    userId: string,
    input: AdminEditUserRequest,
    ifMatch: string,
  ): Promise<AdminUserDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/platform-users/${encodeURIComponent(userId)}/fields`,
      { method: 'PATCH', body: input, headers: { 'if-match': ifMatch } },
    );
    return res.data;
  },

  async requestAvatarUpload(
    userId: string,
    mimeType: string,
    sizeBytes: number,
  ): Promise<{ data: AvatarUploadTicket }> {
    return adminFetch<{ data: AvatarUploadTicket }>(
      `/admin/platform-users/${encodeURIComponent(userId)}/avatar`,
      { method: 'POST', body: { mimeType, sizeBytes } },
    );
  },

  async finalizeAvatar(userId: string, s3Key: string): Promise<AdminUserDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/platform-users/${encodeURIComponent(userId)}/avatar/finalize`,
      { method: 'POST', body: { s3Key } },
    );
    return res.data;
  },

  async removeAvatar(userId: string): Promise<AdminUserDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/platform-users/${encodeURIComponent(userId)}/avatar`,
      { method: 'DELETE', body: {} },
    );
    return res.data;
  },

  /** Full avatar upload: request a presigned PUT → upload the file → finalize. */
  async uploadAvatar(userId: string, file: File): Promise<AdminUserDetail> {
    const ticket = await adminUsersApi.requestAvatarUpload(userId, file.type, file.size);
    const putRes = await fetch(ticket.data.url, {
      method: 'PUT',
      headers: { ...ticket.data.headers },
      body: file,
    });
    if (!putRes.ok) throw new Error(`Avatar upload failed (${putRes.status})`);
    return adminUsersApi.finalizeAvatar(userId, ticket.data.s3Key);
  },

  async suspend(
    userId: string,
    input: SuspendUserInput,
    ifMatch: string,
  ): Promise<AdminUserDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/platform-users/${encodeURIComponent(userId)}/suspend`,
      { method: 'POST', body: input, headers: { 'if-match': ifMatch } },
    );
    return res.data;
  },

  async restore(userId: string, ifMatch: string): Promise<AdminUserDetail> {
    const res = await adminFetch<DetailEnvelope>(
      `/admin/platform-users/${encodeURIComponent(userId)}/restore`,
      { method: 'POST', body: {}, headers: { 'if-match': ifMatch } },
    );
    return res.data;
  },

  /**
   * Super-admin HARD delete (users:delete) — permanently removes the user.
   * If-Match → 409 STALE_USER; user has related data → 409 USER_HAS_DEPENDENCIES.
   */
  async deleteUser(userId: string, reason: string, ifMatch: string): Promise<void> {
    await adminFetch<{ data: { id: string; deleted: boolean } }>(
      `/admin/platform-users/${encodeURIComponent(userId)}`,
      { method: 'DELETE', body: { reason }, headers: { 'if-match': ifMatch } },
    );
  },

  /**
   * F43-FC §6.7 — super-admin BULK hard-delete (users:delete) — ≤100 targets,
   * partial success. POSTs to the dedicated `/admin/platform-users/bulk-delete`
   * route (NOT the `/admin/bulk/*` family, which content_moderator holds). The
   * `Idempotency-Key` header is required server-side (replay returns the same
   * `bulk_action_records` row). Per-target `{ targetId, status, reason }`:
   * deleted → ok; dependency-blocked → failed reason 'USER_HAS_DEPENDENCIES'.
   */
  async bulkDelete(input: BulkActionInput, idempotencyKey: string): Promise<BulkActionResponse> {
    const res = await adminFetch<{ data: BulkActionResponse }>(
      `/admin/platform-users/bulk-delete`,
      { method: 'POST', body: input, headers: { 'idempotency-key': idempotencyKey } },
    );
    return res.data;
  },

  async addNote(userId: string, note: string): Promise<void> {
    await adminFetch<void>(`/admin/platform-users/${encodeURIComponent(userId)}/note`, {
      method: 'POST',
      body: { note },
    });
  },

  async revokeDevice(userId: string, tokenId: string): Promise<void> {
    await adminFetch<void>(
      `/admin/platform-users/${encodeURIComponent(userId)}/devices/${encodeURIComponent(
        tokenId,
      )}/revoke`,
      { method: 'POST', body: {} },
    );
  },

  /**
   * F43-FC §6.6 — admin-suspend a trainer affiliation (`affiliations:moderate`).
   * The cascade cancels the trainer's future bookings at this venue + forces
   * both visibility flags off; the count is surfaced on the result.
   */
  async suspendAffiliation(
    affiliationId: string,
    reason: string,
  ): Promise<AdminAffiliationMutationResult> {
    const res = await adminFetch<{ data: AdminAffiliationMutationResult }>(
      `/admin/affiliations/${encodeURIComponent(affiliationId)}/suspend`,
      { method: 'POST', body: { reason } },
    );
    return res.data;
  },

  /**
   * F43-FC §6.6 — admin-end a trainer affiliation (terminal). The cascade
   * cancels the trainer's future bookings at this venue.
   */
  async endAffiliation(
    affiliationId: string,
    reason: string,
  ): Promise<AdminAffiliationMutationResult> {
    const res = await adminFetch<{ data: AdminAffiliationMutationResult }>(
      `/admin/affiliations/${encodeURIComponent(affiliationId)}/end`,
      { method: 'POST', body: { reason } },
    );
    return res.data;
  },

  /** Nav badge: number of users with active reports against them (KPI proxy). */
  async countActiveReports(): Promise<{ total: number }> {
    const res = await adminUsersApi.list({ hasActiveReports: true, limit: 1 });
    return { total: res.meta.activeReports };
  },
};
